// client/src/pages/Shop/Shop.jsx

import React, { useState, useEffect } from 'react';
import { getAllCards } from '../../api/cardService';
import { buyCards, getAvailablePacks } from '../../api/shopService';
import { useNotification } from '../../hooks/useNotification';
import Card, { CardHeader, CardContent, CardFooter } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Dialog from '../../components/ui/Dialog';
import Loader from '../../components/ui/Loader';
import PackOpening from '../../components/shop/PackOpening';
import './Shop.css';

const Shop = () => {
  const [userCoins, setUserCoins] = useState(1000); 
  const [selectedPack, setSelectedPack] = useState(null);
  const [purchasedCards, setPurchasedCards] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  
  // Utilisation du hook de notification
  const { success, error: showError, warning, info } = useNotification();

  const [packs, setPacks] = useState([
    { id: 'standard', name: 'Pack Standard', description: '5 cartes aléatoires', price: 100, cardCount: 5, guaranteedRarity: 'NORMAL' },
    { id: 'premium', name: 'Pack Premium', description: '5 cartes avec meilleures raretés', price: 250, cardCount: 5, guaranteedRarity: 'RARE' },
    { id: 'ultimate', name: 'Pack Ultime', description: 'Meilleures chances de légendaires', price: 500, cardCount: 5, guaranteedRarity: 'EPIC' },
    { id: 'elemental', name: 'Pack Élémentaire', description: '5 cartes d\'un élément au hasard', price: 300, cardCount: 5, guaranteedRarity: 'RARE' },
  ]);

  // Charger le solde et les packs depuis le serveur
  useEffect(() => {
    const loadData = async () => {
      try {
        const packData = await getAvailablePacks();
        if (packData.success && packData.packs) {
          setPacks(packData.packs);
        }
        if (packData.userCoins !== undefined) {
          setUserCoins(packData.userCoins);
        }
      } catch (err) {
        console.warn('Could not load shop data from server:', err);
      }
    };
    loadData();
  }, []);

  // Sélectionner un pack
  const handleSelectPack = (pack) => {
    setSelectedPack(pack);
    setIsConfirmOpen(true);
  };

  // Acheter un pack
  const handleBuyPack = async () => {
    if (!selectedPack) return;

    if (userCoins < selectedPack.price) {
      showError("Vous n'avez pas assez de pièces pour acheter ce pack.");
      setIsConfirmOpen(false);
      return;
    }

    try {
      setIsLoading(true);
      setIsConfirmOpen(false);

      info(`Achat d'un pack ${selectedPack.name} en cours...`);

      // Appel API pour acheter des cartes
      const response = await buyCards(selectedPack.id);
      const drawnCards = response.cards || response.data?.cards || [];
      setPurchasedCards(drawnCards);
      
      // Update coins from server response or deduct locally
      if (response.remainingCoins !== undefined) {
        setUserCoins(response.remainingCoins);
      } else {
        setUserCoins(userCoins - selectedPack.price);
      }
      
      success(`Pack ${selectedPack.name} acheté avec succès!`);
      
      // Afficher des infos sur les cartes obtenues
      const rarities = countCardRarities(response.cards);
      if (rarities.L > 0 || rarities.SSR > 0) {
        success(`Wow! Vous avez obtenu ${rarities.L} carte(s) Légendaire(s) et ${rarities.SSR} carte(s) Super Super Rare(s)!`);
      } else if (rarities.SR > 0) {
        success(`Super! Vous avez obtenu ${rarities.SR} carte(s) Super Rare(s)!`);
      }
      
      setIsOpening(true);
    } catch (err) {
      showError('Erreur lors de l\'achat du pack. Veuillez réessayer.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Utilitaire pour compter les raretés des cartes
  const countCardRarities = (cards) => {
    return cards.reduce((counts, card) => {
      counts[card.rarity] = (counts[card.rarity] || 0) + 1;
      return counts;
    }, {});
  };

  // Fermer la fenêtre d'ouverture de pack
  const handlePackOpeningComplete = () => {
    setIsOpening(false);
    setPurchasedCards([]);
    setSelectedPack(null);
    info('Cartes ajoutées à votre collection!');
  };

  return (
    <div className="shop-container">
      <h1 className="shop-title">Boutique des Éléments</h1>
        <p className="shop-subtitle">Ouvrez des packs, collectionnez les 505 cartes et forgez votre stratégie.</p>
      
      <div className="user-coins">
        <div className="coin-display">
          <div className="coin-icon">🪙</div>
          <span className="coin-amount">{userCoins}</span>
          <span className="coin-label">pièces</span>
        </div>
        <Button type="primary" size="small" className="buy-coins-button">
          <span className="button-text">Acheter des pièces</span>
          <span className="button-glow"></span>
        </Button>
      </div>
      
      {isLoading ? (
        <div className="shop-loading">
          <Loader text="Traitement de votre achat..." />
        </div>
      ) : isOpening ? (
        <PackOpening 
          cards={purchasedCards}
          packType={selectedPack?.id || 'standard'}
          onComplete={handlePackOpeningComplete}
        />
      ) : (
        <div className="packs-grid">
          {packs.map((pack) => (
            <Card key={pack.id} className="pack-card" hoverable>
              <CardContent className="pack-content">
                <div className="pack-image">
                  <img src={pack.image} alt={pack.name} />
                  <div className="pack-glow"></div>
                </div>
                <div className="pack-info">
                  <h2 className="pack-name">{pack.name}</h2>
                  <p className="pack-description">{pack.description}</p>
                  <div className="pack-details">
                    <span className="pack-cards">{pack.cardCount} cartes</span>
                    <span className="pack-rarity">
                      Min: {pack.guaranteedRarity === 'N' ? 'Normal' : 
                            pack.guaranteedRarity === 'R' ? 'Rare' : 
                            pack.guaranteedRarity === 'SR' ? 'Super Rare' : 
                            pack.guaranteedRarity === 'SSR' ? 'SSR' : 'Légendaire'}
                    </span>
                  </div>
                </div>
                <div className="pack-price">
                  <span className="price-amount">{pack.price}</span>
                  <span className="price-icon">🪙</span>
                </div>
              </CardContent>
              <CardFooter>
                <Button 
                  type={userCoins >= pack.price ? 'primary' : 'outline'} 
                  fullWidth
                  disabled={userCoins < pack.price}
                  onClick={() => handleSelectPack(pack)}
                  className={userCoins >= pack.price ? 'buy-button' : 'insufficient-button'}
                >
                  <span className="button-text">
                    {userCoins >= pack.price ? 'Acheter' : 'Pas assez de pièces'}
                  </span>
                  {userCoins >= pack.price && <span className="button-glow"></span>}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}
      
      <Dialog
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        title="Confirmer l'achat"
        actions={
          <>
            <Button type="outline" onClick={() => setIsConfirmOpen(false)}>Annuler</Button>
            <Button type="primary" onClick={handleBuyPack} className="confirm-button">
              <span className="button-text">Confirmer</span>
              <span className="button-glow"></span>
            </Button>
          </>
        }
      >
        <div className="confirm-purchase">
          <p>Êtes-vous sûr de vouloir acheter <strong>{selectedPack?.name}</strong> pour <strong>{selectedPack?.price} pièces</strong> ?</p>
          <div className="confirm-details">
            <div className="confirm-pack-image">
              <img src={selectedPack?.image} alt={selectedPack?.name} />
            </div>
            <div className="confirm-pack-info">
              <p><strong>Contenu :</strong> {selectedPack?.cardCount} cartes</p>
              <p><strong>Rareté minimale :</strong> {selectedPack?.guaranteedRarity}</p>
              <p><strong>Solde actuel :</strong> {userCoins} pièces</p>
              <p><strong>Solde après achat :</strong> {userCoins - (selectedPack?.price || 0)} pièces</p>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default Shop;